import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  normalizeRole, 
  canAccessClientPortal, 
  canAccessStaffPortal, 
  getPortalUrl, 
  getRoleDisplayName,
  getEmergencyAccess,
  USER_ROLES 
} from '../../utils/roleMapping';

const ProtectedRoute = ({ children, requireRole = null }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50 dark:bg-neutral-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-neutral-600 dark:text-neutral-300">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    // Redirect to login page with return url
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Normalize user role for consistent handling
  const normalizedRole = normalizeRole(user.role);
  const emergencyAccess = getEmergencyAccess(user.role);

  // Check role requirement if specified
  if (requireRole && !requireRole.includes(normalizedRole)) {
    console.error('🚫 ProtectedRoute: Access denied!');
    console.error('🚫 User role:', user.role, 'Normalized role:', normalizedRole);
    console.error('🚫 Required roles:', requireRole);
    console.error('🚫 Current path:', location.pathname);
    
    // Determine which portal the user should be redirected to
    let correctPortal = emergencyAccess.portalUrl;
    let portalName = normalizedRole === USER_ROLES.ADMIN || normalizedRole === USER_ROLES.STAFF ? 'Staff Portal' : 'Client Portal';
    
    // Additional fallback logic
    if (canAccessStaffPortal(normalizedRole)) {
      correctPortal = '/staff';
      portalName = 'Staff Portal';
    } else if (canAccessClientPortal(normalizedRole)) {
      correctPortal = '/portal';
      portalName = 'Client Portal';
    } else {
      // Emergency fallback - always allow client portal access
      correctPortal = '/portal';
      portalName = 'Client Portal';
      console.warn('🚨 Emergency fallback activated for user:', user.email, 'with role:', user.role);
    }
    
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50 dark:bg-neutral-900">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="text-6xl mb-4">🚫</div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 mb-2">
            Access Denied
          </h1>
          <p className="text-neutral-600 dark:text-neutral-300 mb-4">
            You don't have permission to access this portal.
          </p>
          <p className="text-neutral-500 dark:text-neutral-400 mb-6">
            Based on your role ({getRoleDisplayName(normalizedRole)}), you should access the {portalName}.
          </p>
          {emergencyAccess.isEmergencyFallback && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-yellow-800">
                ⚠️ Your role ({user.role}) has been automatically mapped to Client Portal access.
              </p>
            </div>
          )}
          <div className="space-y-3">
            <button
              onClick={() => window.location.href = correctPortal}
              className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors duration-200 font-medium"
            >
              Go to {portalName}
            </button>
            <button
              onClick={() => window.history.back()}
              className="w-full bg-gray-200 text-gray-800 px-6 py-3 rounded-lg hover:bg-gray-300 transition-colors duration-200 font-medium"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return children;
};

export default ProtectedRoute;
